/**
 * Safe API error formatter — turns any axios error into a plain string.
 *
 * Prevents React error #31 ("Objects are not valid as a React child") when
 * FastAPI returns 422 with a Pydantic error list of {type, loc, msg, input}.
 * Without this, passing that array/object into <toast /> crashes the entire
 * React tree and shows a blank screen.
 *
 * Feb 2026 — traduce los mensajes técnicos de Pydantic v2 al español para
 * que el admin/editor vea exactamente qué campo ha fallado y por qué,
 * sin jerga en inglés.
 */

// Traducción de los `type` de error de Pydantic v2 al español. Si el mensaje
// original contiene placeholders como ">= 5", los preservamos parseando el
// `ctx` que Pydantic añade cuando aplica.
const PYDANTIC_TYPE_ES = {
  // Tipos básicos
  string_type: 'Debe ser un texto válido',
  int_type: 'Debe ser un número entero',
  int_parsing: 'Debe ser un número entero válido',
  float_type: 'Debe ser un número decimal',
  float_parsing: 'Debe ser un número decimal válido',
  bool_type: 'Debe ser verdadero o falso',
  bool_parsing: 'Debe ser verdadero o falso',
  list_type: 'Debe ser una lista',
  dict_type: 'Debe ser un objeto (diccionario)',
  set_type: 'Debe ser un conjunto',
  tuple_type: 'Debe ser una tupla',
  bytes_type: 'Debe ser una secuencia de bytes',
  none_required: 'Debe estar vacío (null)',

  // Campos obligatorios / extras
  missing: 'Este campo es obligatorio',
  extra_forbidden: 'Este campo no está permitido',
  model_type: 'El objeto enviado no coincide con el modelo esperado',

  // Fechas y URLs
  datetime_type: 'Debe ser una fecha/hora válida',
  datetime_parsing: 'Formato de fecha/hora inválido',
  date_type: 'Debe ser una fecha válida',
  date_parsing: 'Formato de fecha inválido',
  time_type: 'Debe ser una hora válida',
  time_parsing: 'Formato de hora inválido',
  url_type: 'Debe ser una URL válida',
  url_parsing: 'URL con formato inválido',
  url_scheme: 'La URL debe empezar por http:// o https://',
  uuid_type: 'Debe ser un UUID válido',
  uuid_parsing: 'UUID con formato inválido',

  // Longitud / rangos
  string_too_short: 'El texto es demasiado corto',
  string_too_long: 'El texto es demasiado largo',
  string_pattern_mismatch: 'El texto no cumple el formato requerido',
  too_short: 'Faltan elementos (demasiado corto)',
  too_long: 'Sobran elementos (demasiado largo)',
  greater_than: 'El valor debe ser mayor',
  greater_than_equal: 'El valor debe ser mayor o igual',
  less_than: 'El valor debe ser menor',
  less_than_equal: 'El valor debe ser menor o igual',
  multiple_of: 'El valor debe ser múltiplo del permitido',
  finite_number: 'El número debe ser finito',

  // Otros comunes
  literal_error: 'El valor no está entre las opciones permitidas',
  enum: 'El valor no está entre las opciones permitidas',
  assertion_error: 'Valor inválido',
  json_invalid: 'JSON con formato inválido',
  json_type: 'Debe ser JSON válido',
};

// Nombres de campo → etiqueta amigable en español. Solo campos frecuentes;
// los que no estén aquí se muestran tal cual (útil para admin/dev).
const FIELD_LABEL_ES = {
  expense_invoice_file: 'Factura de gasto (PDF)',
  invoice_income_number: 'Nº factura ingreso',
  invoice_expense_number: 'Nº factura gasto',
  customer_id: 'Cliente',
  customer_email: 'Email del cliente',
  customer_name: 'Nombre del cliente',
  product_id: 'Medio (producto)',
  product_url: 'URL del medio',
  writer_id: 'Redactor',
  writer_email: 'Email del redactor',
  url_1: 'URL 1',
  url_2: 'URL 2',
  anchor_text_1: 'Anchor Text 1',
  anchor_text_2: 'Anchor Text 2',
  pc: 'PC',
  pvp: 'PVP',
  pc_sensitive: 'PC sensible',
  pvp_sensitive: 'PVP sensible',
  word_count: 'Nº de palabras',
  status: 'Estado',
  order_date: 'Fecha del pedido',
  final_date: 'Fecha final',
  send_date: 'Fecha de envío',
  deadline: 'Fecha límite',
  collection_date: 'Fecha de cobro',
  invoice_date: 'Fecha de factura',
  payment_deadline: 'Fecha límite de pago',
  incident_reason: 'Motivo de incidencia',
  motivo_cancelacion: 'Motivo de cancelación',
  published_url: 'URL publicada',
  internal_notes: 'Notas internas',
  description: 'Descripción',
  writer_instructions: 'Instrucciones para el redactor',
  tipo_pedido: 'Tipo de pedido',
};

const prettyField = (field) => {
  if (!field) return '';
  if (FIELD_LABEL_ES[field]) return FIELD_LABEL_ES[field];
  // Fallback: convertir snake_case → "Snake case"
  return field
    .replace(/[_.]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const translatePydanticError = (e) => {
  if (typeof e === 'string') return e;
  const type = e?.type || '';
  const ctx = e?.ctx || {};
  const original = e?.msg || e?.message || '';

  let translated = PYDANTIC_TYPE_ES[type];

  // Enriquecer con contexto (rangos, mínimos, patrones…)
  if (translated) {
    if (type === 'greater_than' && ctx.gt !== undefined) {
      translated += ` que ${ctx.gt}`;
    } else if (type === 'greater_than_equal' && ctx.ge !== undefined) {
      translated += ` a ${ctx.ge}`;
    } else if (type === 'less_than' && ctx.lt !== undefined) {
      translated += ` que ${ctx.lt}`;
    } else if (type === 'less_than_equal' && ctx.le !== undefined) {
      translated += ` a ${ctx.le}`;
    } else if (type === 'string_too_short' && ctx.min_length !== undefined) {
      translated += ` (mínimo ${ctx.min_length} caracteres)`;
    } else if (type === 'string_too_long' && ctx.max_length !== undefined) {
      translated += ` (máximo ${ctx.max_length} caracteres)`;
    } else if (type === 'too_short' && ctx.min_length !== undefined) {
      translated += ` (mínimo ${ctx.min_length} elementos)`;
    } else if (type === 'too_long' && ctx.max_length !== undefined) {
      translated += ` (máximo ${ctx.max_length} elementos)`;
    } else if ((type === 'literal_error' || type === 'enum') && ctx.expected) {
      translated += `. Opciones: ${ctx.expected}`;
    } else if (type === 'string_pattern_mismatch' && ctx.pattern) {
      translated += ` (patrón: ${ctx.pattern})`;
    }
  } else if (type.startsWith('value_error')) {
    // value_error incluye el mensaje custom del validador. Lo pasamos tal cual
    // quitando el prefijo "Value error, " que añade Pydantic.
    translated = original.replace(/^Value error,\s*/i, '') || 'Valor inválido';
  } else {
    // Tipo desconocido — dejamos el mensaje original en inglés como fallback.
    translated = original || 'Error de validación';
  }

  return translated;
};

const formatApiError = (error, fallbackOrOpts = 'Algo ha fallado') => {
  // Feb 2026 — second arg may be:
  //   (a) legacy string fallback (backward compatible)
  //   (b) options object `{ tx, fallback }` — enables ES→other-language
  //       mapping for known HTTPException details.
  let fallback = 'Algo ha fallado';
  let tx = null;
  if (typeof fallbackOrOpts === 'string') {
    fallback = fallbackOrOpts;
  } else if (fallbackOrOpts && typeof fallbackOrOpts === 'object') {
    fallback = fallbackOrOpts.fallback || fallback;
    tx = typeof fallbackOrOpts.tx === 'function' ? fallbackOrOpts.tx : null;
  }
  const translate = (es) => {
    if (!es) return es;
    if (!tx) return es;
    // Look for a known-error exact match or prefix match
    const trimmed = String(es).trim();
    if (KNOWN_HTTP_ERRORS_ES_EN.has(trimmed)) {
      return tx(trimmed, KNOWN_HTTP_ERRORS_ES_EN.get(trimmed));
    }
    for (const [esKey, enVal] of KNOWN_HTTP_ERRORS_ES_EN) {
      if (trimmed.startsWith(esKey)) {
        return trimmed.replace(esKey, tx(esKey, enVal));
      }
    }
    return es;
  };
  // Feb 2026 — allow calling with a raw string (bypasses axios extraction)
  if (typeof error === 'string') {
    return translate(error);
  }
  const d = error?.response?.data?.detail;
  if (!d) return translate(error?.message || fallback);
  if (typeof d === 'string') return translate(d);
  if (Array.isArray(d)) {
    return (
      d
        .map((e) => {
          if (typeof e === 'string') return translate(e);
          const field = Array.isArray(e?.loc)
            ? e.loc.filter((p) => p !== 'body' && typeof p === 'string').join('.')
            : '';
          const msg = translatePydanticError(e);
          return field ? `${prettyField(field)}: ${msg}` : msg;
        })
        .filter(Boolean)
        .join(' · ') || fallback
    );
  }
  if (typeof d === 'object') {
    return translate(d.msg || d.message || JSON.stringify(d));
  }
  return translate(String(d));
};

// Feb 2026 — ES→EN dictionary of common HTTPException details raised by the
// backend that reach client/editor toasts. The i18n clientTxDict extends
// these to de/fr/it/pt/zh/ar automatically when the caller passes `tx`.
const KNOWN_HTTP_ERRORS_ES_EN = new Map([
  ['Email ya registrado', 'Email already registered'],
  ['Este email ya tiene una cuenta de editor. Usa otro correo o inicia sesión como editor.',
   'This email already has an editor account. Use a different email or sign in as editor.'],
  ['Este email ya tiene una cuenta de cliente. Usa otro correo o inicia sesión como cliente.',
   'This email already has a client account. Use a different email or sign in as client.'],
  ['Credenciales inválidas', 'Invalid credentials'],
  ['Cuenta bloqueada temporalmente. Inténtalo más tarde.',
   'Account temporarily locked. Try again later.'],
  ['Cuenta bloqueada', 'Account locked'],
  ['Email no verificado', 'Email not verified'],
  ['La contraseña debe tener al menos 8 caracteres',
   'Password must be at least 8 characters'],
  ['Token inválido o expirado', 'Invalid or expired token'],
  ['Enlace inválido o caducado. Solicita uno nuevo.',
   'Invalid or expired link. Request a new one.'],
  ['Pedido no encontrado', 'Order not found'],
  ['Producto no encontrado', 'Product not found'],
  ['Cliente no encontrado', 'Client not found'],
  ['Editor no encontrado', 'Publisher not found'],
  ['Editor no encontrado o sin acceso', 'Publisher not found or no access'],
  ['Editor sombra no encontrado', 'Shadow publisher not found'],
  ['Saldo insuficiente en el monedero', 'Insufficient wallet balance'],
  ['Saldo insuficiente', 'Insufficient balance'],
  ['Cupón caducado', 'Expired coupon'],
  ['Cupón no válido', 'Invalid coupon'],
  ['Este cupón ya no está disponible', 'This coupon is no longer available'],
  ['Archivo vacío', 'Empty file'],
  ['Archivo no encontrado', 'File not found'],
  ['Adjunto no encontrado', 'Attachment not found'],
  ['El PDF es demasiado grande', 'The PDF is too large'],
  ['Dominio inválido', 'Invalid domain'],
  ['Error creando ticket', 'Error creating ticket'],
  ['Este motivo ya fue publicado al cliente. No se puede descartar.',
   'This reason was already published to the client. It cannot be discarded.'],
  ['Debes aceptar la política de privacidad',
   'You must accept the privacy policy'],
  ['Debes aceptar los términos y la política de privacidad',
   'You must accept the terms and the privacy policy'],
  ['Error de conexión', 'Connection error'],
  ['Ha ocurrido un error. Inténtalo de nuevo.',
   'Something went wrong. Please try again.'],
]);

export default formatApiError;
export { formatApiError };
